import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { 
  Zap, 
  DollarSign, 
  Clock, 
  Shield, 
  Smartphone, 
  TrendingUp,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  ArrowRight,
  Star,
  Users,
  BarChart3,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';
import axios from 'axios';
import './App.css';

const API_BASE = 'http://3.34.225.8:3001';

// 카운트다운 컴포넌트
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const targetDate = new Date('2025-06-26T23:59:59').getTime();

    const updateTimer = () => {
      const now = new Date().getTime();
      const distance = targetDate - now;

      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="countdown-timer">
      <h3>⏰ 모두 서비스 종료까지</h3>
      <div className="countdown-grid">
        <div className="countdown-item">
          <span className="countdown-number">{timeLeft.days}</span>
          <span className="countdown-label">일</span>
        </div>
        <div className="countdown-item">
          <span className="countdown-number">{timeLeft.hours}</span>
          <span className="countdown-label">시간</span>
        </div>
        <div className="countdown-item">
          <span className="countdown-number">{timeLeft.minutes}</span>
          <span className="countdown-label">분</span>
        </div>
        <div className="countdown-item">
          <span className="countdown-number">{timeLeft.seconds}</span>
          <span className="countdown-label">초</span>
        </div>
      </div>
    </div>
  );
};

// 홈 섹션 (메인 화면)
const HomeSection = ({ onAnalyze, register, errors }) => (
  <section className="hero premium-hero">
    <div className="hero-background">
      <div className="floating-particles"></div>
    </div>
    
    <div className="container">
      <motion.div 
        className="hero-content"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div 
          className="hero-badge urgent"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <AlertCircle size={16} />
          <span>🚨 네이버 모두 6월 26일 종료!</span>
        </motion.div>
        
        <h1 className="hero-title premium-title">
          모두 종료, 걱정 끝!<br />
          <span className="highlight-premium">3분 자동 이전</span>
          <br />+ 파격 가격
        </h1>
        
        <p className="hero-description premium-desc">
          다른 곳은 사이트만 옮겨드려요.<br />
          <strong>위아비즈는 사이트 이전 + 자동 마케팅까지!</strong><br />
          쇼츠 영상, 블로그 포스팅 모두 자동으로 매출까지 올려드립니다!
        </p>

        <CountdownTimer />

        <div className="transfer-form premium-form">
          <form onSubmit={onAnalyze} className="url-form">
            <div className="input-group premium-input">
              <motion.input
                type="url"
                placeholder="모두 사이트 주소를 입력하세요 (예: https://example.modoo.at)"
                {...register('modooUrl', {
                  required: '모두 사이트 주소를 입력해주세요',
                  pattern: {
                    value: /.*\.modoo\.at.*/,
                    message: '올바른 모두 사이트 주소를 입력해주세요'
                  }
                })}
                whileFocus={{ scale: 1.02 }}
              />
              <motion.button
                type="submit"
                className="analyze-btn premium-btn"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Zap size={20} />
                <span>3분 자동 이전 시작!</span>
                <div className="btn-glow"></div>
              </motion.button>
            </div>
            {errors.modooUrl && (
              <motion.p 
                className="error-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {errors.modooUrl.message}
              </motion.p>
            )}
          </form>
        </div>

        <div className="trust-signals premium-trust">
          <motion.div 
            className="trust-item"
            whileHover={{ scale: 1.05 }}
          >
            <CheckCircle size={16} />
            <span>이전 비용 무료</span>
          </motion.div>
          <motion.div 
            className="trust-item"
            whileHover={{ scale: 1.05 }}
          >
            <Clock size={16} />
            <span>3분 내 완료</span>
          </motion.div>
          <motion.div 
            className="trust-item premium-price"
            whileHover={{ scale: 1.05 }}
          >
            <DollarSign size={16} />
            <span>월 7,700원 (🍀 행운의 가격!)</span>
          </motion.div>
        </div>
      </motion.div>

      <motion.div 
        className="hero-stats premium-stats"
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <div className="stat-card premium-stat">
          <Users size={24} />
          <div className="stat-content">
            <h3>1,247+</h3>
            <p>이전 완료</p>
          </div>
          <div className="stat-sparkle"></div>
        </div>
        <div className="stat-card premium-stat">
          <Clock size={24} />
          <div className="stat-content">
            <h3>156초</h3>
            <p>평균 이전시간</p>
          </div>
          <div className="stat-sparkle"></div>
        </div>
        <div className="stat-card premium-stat">
          <Star size={24} />
          <div className="stat-content">
            <h3>4.8/5</h3>
            <p>고객 만족도</p>
          </div>
          <div className="stat-sparkle"></div>
        </div>
      </motion.div>
    </div>
  </section>
);

// 분석 중 섹션
const AnalyzingSection = () => (
  <section className="processing">
    <div className="container">
      <motion.div 
        className="processing-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          className="loading-spinner premium-spinner"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Zap size={48} />
        </motion.div>
        <h2>모두 사이트 분석 중...</h2>
        <p>사이트 구조, 콘텐츠, 이미지를 자동으로 분석하고 있습니다.</p>
        <div className="progress-steps">
          <div className="step active">사이트 접속</div>
          <div className="step active">콘텐츠 추출</div>
          <div className="step">업종 판별</div>
        </div>
      </motion.div>
    </div>
  </section>
);

// 이전 정보 입력 섹션
const TransferFormSection = ({ analysisResult, onTransfer }) => {
  const { register, handleSubmit, formState: { errors } } = useForm();

  return (
    <section className="transfer-form-section">
      <div className="container">
        <motion.div 
          className="form-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2>✅ 분석 완료! 이전 정보를 입력해주세요</h2>
          
          {analysisResult && (
            <div className="analysis-preview">
              <h3>분석 결과</h3>
              <p>업종: {analysisResult.category || '일반'}</p>
              <p>페이지 수: {analysisResult.pageCount || '5'}개</p>
              <p>예상 이전 시간: 3분</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onTransfer)} className="customer-form">
            <div className="form-group">
              <label>사업체명 *</label>
              <input
                type="text"
                {...register('businessName', { required: '사업체명을 입력해주세요' })}
                placeholder="예: 맛있는 떡볶이"
              />
              {errors.businessName && <span className="error">{errors.businessName.message}</span>}
            </div>

            <div className="form-group">
              <label>원하는 도메인명 *</label>
              <div className="domain-input">
                <input
                  type="text"
                  {...register('subdomain', { 
                    required: '도메인명을 입력해주세요',
                    pattern: {
                      value: /^[a-z0-9-]+$/,
                      message: '영문 소문자, 숫자, 하이픈만 사용 가능'
                    }
                  })}
                  placeholder="예: delicious-tteok"
                />
                <span>.wiabiz.com</span>
              </div>
              {errors.subdomain && <span className="error">{errors.subdomain.message}</span>}
            </div>

            <div className="form-group">
              <label>연락처 *</label>
              <input
                type="tel"
                {...register('phone', { required: '연락처를 입력해주세요' })}
                placeholder="예: 010-1234-5678"
              />
              {errors.phone && <span className="error">{errors.phone.message}</span>}
            </div>

            <div className="form-group">
              <label>이메일</label>
              <input
                type="email"
                {...register('email')}
                placeholder="예: contact@business.com (선택사항)"
              />
            </div>

            <button type="submit" className="submit-btn">
              <ArrowRight size={20} />
              이전 시작하기
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

// 이전 진행 중 섹션
const TransferringSection = () => (
  <section className="transferring">
    <div className="container">
      <motion.div 
        className="transferring-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          className="transfer-spinner"
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        >
          <Smartphone size={48} />
        </motion.div>
        <h2>🚀 사이트 이전 중...</h2>
        <p>모두 사이트를 위아비즈로 이전하고 있습니다.</p>
        
        <div className="transfer-steps">
          <div className="step completed">✅ 콘텐츠 복사</div>
          <div className="step active">🔄 디자인 적용</div>
          <div className="step">📱 PWA 변환</div>
          <div className="step">🔍 SEO 최적화</div>
        </div>

        <div className="estimated-time">
          <Clock size={16} />
          <span>예상 완료 시간: 2분 30초</span>
        </div>
      </motion.div>
    </div>
  </section>
);

// 이전 완료 섹션
const CompletedSection = ({ transferResult }) => (
  <section className="completed">
    <div className="container">
      <motion.div 
        className="completed-content"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
      >
        <motion.div
          className="success-icon"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <CheckCircle size={64} />
        </motion.div>
        
        <h2>🎉 이전 완료!</h2>
        <p>모두 사이트가 성공적으로 위아비즈로 이전되었습니다!</p>

        {transferResult && (
          <div className="result-info">
            <div className="new-site-url">
              <label>새 사이트 주소:</label>
              <a 
                href={`https://${transferResult.subdomain}.wiabiz.com`}
                target="_blank"
                rel="noopener noreferrer"
                className="site-link"
              >
                https://{transferResult.subdomain}.wiabiz.com
                <ExternalLink size={16} />
              </a>
            </div>
            
            <div className="next-steps">
              <h3>✅ 완료된 작업</h3>
              <ul>
                <li>사이트 콘텐츠 이전</li>
                <li>반응형 디자인 적용</li>
                <li>PWA 앱 설치 기능 추가</li>
                <li>SEO 메타태그 최적화</li>
                <li>SSL 보안인증서 적용</li>
              </ul>
            </div>
          </div>
        )}

        <div className="action-buttons">
          <motion.a
            href={transferResult ? `https://${transferResult.subdomain}.wiabiz.com` : '#'}
            className="btn primary"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ExternalLink size={20} />
            새 사이트 확인하기
          </motion.a>
          
          <motion.button
            className="btn secondary"
            onClick={() => window.location.reload()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            다른 사이트 이전하기
          </motion.button>
        </div>

        <div className="premium-offer">
          <h3>🚀 프리미엄 마케팅 자동화</h3>
          <p>이제 쇼츠 영상, 블로그 포스팅까지 자동으로!</p>
          <button className="premium-btn">
            프리미엄 기능 알아보기
          </button>
        </div>
      </motion.div>
    </div>
  </section>
);

// 차별화 기능 섹션
const DifferentiatorSection = () => (
  <section className="differentiator-section" id="differentiator">
    <div className="container">
      <motion.div 
        className="section-header"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="section-title premium-section-title">
          🚀 위아비즈만의 <span className="highlight">킬러 기능</span>
        </h2>
        <p className="section-subtitle">다른 곳은 단순 이전만! 위아비즈는 이전 + 마케팅 자동화까지!</p>
      </motion.div>

      <div className="differentiator-grid">
        <motion.div 
          className="diff-card killer-feature"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          <div className="diff-icon">🎬</div>
          <h3>동영상도 무제한!</h3>
          <p>유튜브, 네이버TV 등<br/>모든 동영상 퍼가기 지원!</p>
          <div className="diff-badge">타사 불가</div>
        </motion.div>

        <motion.div 
          className="diff-card killer-feature"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <div className="diff-icon">🔑</div>
          <h3>네이버 로그인으로 간편 시작!</h3>
          <p>모두 사용자 친화적<br/>원클릭 로그인!</p>
          <div className="diff-badge">독점 기능</div>
        </motion.div>

        <motion.div 
          className="diff-card killer-feature"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <div className="diff-icon">📱</div>
          <h3>앱 설치까지 자동!</h3>
          <p>PWA 기술로<br/>고객 홈화면에 바로!</p>
          <div className="diff-badge">자동화</div>
        </motion.div>

        <motion.div 
          className="diff-card killer-feature"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          <div className="diff-icon">🔍</div>
          <h3>SEO 최적화 자동!</h3>
          <p>검색 노출 향상<br/>자동 메타태그 생성!</p>
          <div className="diff-badge">무료 포함</div>
        </motion.div>

        <motion.div 
          className="diff-card killer-feature"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <div className="diff-icon">🎨</div>
          <h3>고급 템플릿 무료!</h3>
          <p>업종별 전문 디자인<br/>색상/폰트 커스터마이징!</p>
          <div className="diff-badge">프리미엄</div>
        </motion.div>

        <motion.div 
          className="diff-card killer-feature ultimate"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
        >
          <div className="diff-icon">🚀</div>
          <h3>마케팅 자동화 포함!</h3>
          <p>쇼츠 영상, 블로그 포스팅<br/>모두 자동으로 매출까지!</p>
          <div className="diff-badge ultimate-badge">킬러 기능</div>
        </motion.div>
      </div>

      <motion.div 
        className="differentiator-footer"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.8 }}
      >
        <div className="comparison-text">
          <h4>💡 타사 vs 위아비즈</h4>
          <div className="comparison">
            <div className="vs-item">
              <span className="others">다른 곳: 사이트만 옮겨드림</span>
              <span className="vs">VS</span>
              <span className="wiabiz">위아비즈: 이전 + 마케팅 자동화!</span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// 기능 섹션
const FeaturesSection = () => (
  <section id="features" className="features">
    <div className="container">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        AI 프리미엄 마케팅 자동화
      </motion.h2>
      
      <div className="features-grid">
        <motion.div 
          className="feature-card"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          <div className="feature-icon">🎬</div>
          <h3>AI 쇼츠 영상 자동 생성</h3>
          <p>월 19,900원</p>
          <ul>
            <li>✅ 콘텐츠 기반 쇼츠 제작</li>
            <li>✅ 인스타그램/유튜브 자동 업로드</li>
            <li>✅ 트렌드 해시태그 자동 추가</li>
          </ul>
        </motion.div>

        <motion.div 
          className="feature-card"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <div className="feature-icon">📝</div>
          <h3>AI 블로그 자동 포스팅</h3>
          <p>월 14,900원</p>
          <ul>
            <li>✅ 업종별 맞춤 콘텐츠</li>
            <li>✅ SEO 최적화 자동 적용</li>
            <li>✅ 네이버/티스토리 동시 발행</li>
          </ul>
        </motion.div>

        <motion.div 
          className="feature-card"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <div className="feature-icon">📱</div>
          <h3>AI SNS 자동화</h3>
          <p>월 12,900원</p>
          <ul>
            <li>✅ 인스타그램/페이스북 자동 포스팅</li>
            <li>✅ 콘텐츠 일정 관리</li>
            <li>✅ 해시태그 최적화</li>
          </ul>
        </motion.div>

        <motion.div 
          className="feature-card"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          <div className="feature-icon">🧵</div>
          <h3>AI 스레드 자동화</h3>
          <p>월 9,900원</p>
          <ul>
            <li>✅ 실시간 트렌드 포스팅</li>
            <li>✅ 커뮤니티 참여 자동화</li>
            <li>✅ 브랜드 인지도 확산</li>
          </ul>
        </motion.div>

        <motion.div 
          className="feature-card"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <div className="feature-icon">🎯</div>
          <h3>AI 네이버 카페 마케팅</h3>
          <p>월 16,900원</p>
          <ul>
            <li>✅ 관련 카페 자동 발굴</li>
            <li>✅ 고품질 포스팅 작성</li>
            <li>✅ 카페 운영진과 관계 관리</li>
          </ul>
        </motion.div>

        <motion.div 
          className="feature-card premium"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
        >
          <div className="premium-badge">💎 풀패키지</div>
          <div className="feature-icon">🚀</div>
          <h3>AI 전체 자동화</h3>
          <p><span className="original-price">73,500원</span> 월 49,900원</p>
          <ul>
            <li>✅ 위 모든 기능 포함</li>
            <li>✅ 32% 할인 혜택</li>
            <li>✅ 우선 고객지원</li>
            <li>✅ 월간 성과 리포트</li>
          </ul>
        </motion.div>
      </div>
    </div>
  </section>
);

// 가격 비교 섹션
const PricingSection = () => (
  <section id="pricing" className="pricing premium-pricing">
    <div className="container">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        가격 비교
      </motion.h2>
      <div className="pricing-grid">
        <motion.div 
          className="pricing-card competitor"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          <h3>아*웹</h3>
          <div className="price">월 16,000원~</div>
          <ul>
            <li>기본 이전만</li>
            <li>추가 기능 별도 결제</li>
            <li>복잡한 설정</li>
            <li>제한적 커스터마이징</li>
          </ul>
        </motion.div>
        
        <motion.div 
          className="pricing-card competitor"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <h3>Za**it</h3>
          <div className="price">월 30,000원~</div>
          <ul>
            <li>기본 이전만</li>
            <li>AI 기능 추가 비용</li>
            <li>리터칭 서비스 별도</li>
            <li>높은 러닝커브</li>
          </ul>
        </motion.div>
        
        <motion.div 
          className="pricing-card recommended premium-card"
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <div className="badge premium-badge">🍀 행운의 가격</div>
          <h3>위아비즈</h3>
          <div className="price premium-price-text">월 7,700원</div>
          <ul>
            <li>✅ 무료 이전</li>
            <li>✅ 마케팅 자동화</li>
            <li>✅ 쇼츠/블로그 자동 생성</li>
            <li>✅ 24시간 고객지원</li>
            <li>✅ 합리적 사용 트래픽*</li>
          </ul>
          <div className="savings premium-savings">
            🍀 연간 최대 27만원 절약!
          </div>
          <small className="traffic-note">
            * 일반적인 비즈니스 사용량 기준 (월 10GB 트래픽)
          </small>
        </motion.div>
      </div>
    </div>
  </section>
);

// 통계 섹션
const StatsSection = () => (
  <section className="stats">
    <div className="container">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        위아비즈 성과
      </motion.h2>
      
      <div className="stats-grid">
        <motion.div 
          className="stat-item"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          <BarChart3 size={32} />
          <h3>1,247+</h3>
          <p>이전 완료 사이트</p>
        </motion.div>

        <motion.div 
          className="stat-item"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <Clock size={32} />
          <h3>156초</h3>
          <p>평균 이전 시간</p>
        </motion.div>

        <motion.div 
          className="stat-item"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <Star size={32} />
          <h3>4.8/5</h3>
          <p>고객 만족도</p>
        </motion.div>

        <motion.div 
          className="stat-item"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          <TrendingUp size={32} />
          <h3>89%</h3>
          <p>프리미엄 업그레이드율</p>
        </motion.div>
      </div>
    </div>
  </section>
);

// 메인 App 컴포넌트
function App() {
  const [step, setStep] = useState('home');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [transferResult, setTransferResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  // 모두 사이트 분석
  const analyzeSite = async (data) => {
    setIsLoading(true);
    setStep('analyzing');
    
    try {
      const response = await axios.post('/api/analyze', {
        url: data.modooUrl
      });
      
      if (response.data.success) {
        setAnalysisResult(response.data.preview);
        setStep('transfer-form');
        toast.success('사이트 분석 완료!');
      }
    } catch (error) {
      console.error('API Error:', error);
      toast.error(error.response?.data?.error || '사이트 분석에 실패했습니다. 다시 시도해주세요.');
      setStep('home');
    } finally {
      setIsLoading(false);
    }
  };

  // 실제 이전 실행
  const executeTransfer = async (customerInfo) => {
    setIsLoading(true);
    setStep('transferring');
    
    try {
      const response = await axios.post('/api/transfer', {
        url: analysisResult.originalUrl || register.modooUrl,
        customerInfo
      });
      
      if (response.data.success) {
        setTransferResult(response.data);
        setStep('completed');
        toast.success('🎉 이전 완료!');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || '이전 중 오류가 발생했습니다.');
      setStep('transfer-form');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="App">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="header">
        <div className="container">
          <motion.div 
            className="logo"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h1>위아비즈</h1>
            <span>우리들의 비즈니스</span>
          </motion.div>
          
          <nav className="nav">
            <a href="#features">서비스</a>
            <a href="#pricing">가격</a>
            <a href="#contact">문의</a>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main>
        {step === 'home' && <HomeSection onAnalyze={handleSubmit(analyzeSite)} register={register} errors={errors} />}
        {step === 'analyzing' && <AnalyzingSection />}
        {step === 'transfer-form' && <TransferFormSection analysisResult={analysisResult} onTransfer={executeTransfer} />}
        {step === 'transferring' && <TransferringSection />}
        {step === 'completed' && <CompletedSection transferResult={transferResult} />}
      </main>

      {/* Features Section */}
      <DifferentiatorSection />
      <FeaturesSection />
      
      {/* Pricing Section */}
      <PricingSection />
      
      {/* Stats Section */}
      <StatsSection />
      
      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>위아비즈</h3>
              <p>모두 종료, 걱정 끝!<br />3분 자동 이전 + 파격 가격</p>
            </div>
            <div className="footer-section">
              <h4>서비스</h4>
              <ul>
                <li><a href="#transfer">모두 이전</a></li>
                <li><a href="#hosting">저가 호스팅</a></li>
                <li><a href="#premium">프리미엄 자동화</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>고객지원</h4>
              <ul>
                <li><a href="tel:1599-1045">1599-1045</a></li>
                <li><a href="mailto:support@wiabiz.com">support@wiabiz.com</a></li>
                <li><a href="#faq">자주 묻는 질문</a></li>
              </ul>
            </div>
          </div>
          
          {/* 사업자 정보 */}
          <div className="company-info">
            <div className="company-details">
              <p><strong>상호:</strong> (주)스마일스토리</p>
              <p><strong>대표이사:</strong> 연삼흠</p>
              <p><strong>사업자등록번호:</strong> 109-86-17229</p>
              <p><strong>통신판매업신고번호:</strong> 2013-서울강서-0434</p>
              <p><strong>개인정보책임자:</strong> 이유정</p>
              <p><strong>주소:</strong> (07801) 서울시 강서구 마곡중앙6로 21, 5층 511호 디01(마곡동, 이너매스마곡1차)</p>
              <p><strong>Tel:</strong> 1599-1045</p>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; 2025 WIA Biz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;